import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { apiService } from '../services/api';

const AddBanner = ({ onSuccess }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    cta: '',
    cta_url: '',
    image: null,
  });
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const showAlert = (message, severity = 'success') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateFile = (file, maxSizeMB = 10) => {
    if (!file) {
      showAlert('❌ Please select an image', 'error');
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      showAlert(`❌ File size must be less than ${maxSizeMB}MB`, 'error');
      return false;
    }
    if (!file.type.startsWith('image/')) {
      showAlert('❌ Please select a valid image file', 'error');
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!validateFile(file)) return;

    setForm((prev) => ({ ...prev, image: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.image) {
      showAlert('❌ Please fill all required fields', 'error');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('cta', form.cta);
      formData.append('cta_url', form.cta_url);
      formData.append('image', form.image);

      const response = await apiService.createBannerWithImage(formData);
      if (response.success) {
        showAlert('✅ Banner added successfully!');
        setForm({ title: '', description: '', cta: '', cta_url: '', image: null });
        setPreview('');
        if (onSuccess) onSuccess();
      } else {
        showAlert('❌ Error: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showAlert('❌ Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              label="Banner Title" 
              name="title" 
              value={form.title} 
              onChange={handleChange} 
              fullWidth 
              required 
            />
            <TextField 
              label="Description" 
              name="description" 
              value={form.description} 
              onChange={handleChange} 
              fullWidth 
              required 
              multiline 
              rows={2} 
            />
            <TextField 
              label="CTA Button Text" 
              name="cta" 
              value={form.cta} 
              onChange={handleChange} 
              fullWidth 
              placeholder="e.g., Learn More, Shop Now"
            />
            <TextField 
              label="CTA Button URL" 
              name="cta_url" 
              value={form.cta_url} 
              onChange={handleChange} 
              fullWidth 
              placeholder="https://bisauli.com"
              type="url"
            />

            <Box sx={{ border: '2px dashed #667eea', borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} id="banner-image-input" />
              <label htmlFor="banner-image-input" style={{ cursor: 'pointer', display: 'block' }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Click to upload banner image</Typography>
                <Typography variant="caption" color="text.secondary">{form.image ? form.image.name : 'PNG, JPG, GIF up to 10MB'}</Typography>
              </label>
            </Box>

            <Button type="submit" variant="contained" sx={{ bgcolor: '#667eea', mt: 2 }} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : '➕ Add Banner'}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          {preview && (
            <Card sx={{ boxShadow: 2 }}>
              <CardMedia component="img" height="250" image={preview} alt="Banner preview" />
              <Box sx={{ p: 2 }}>
                <Typography variant="h6">{form.title || 'Preview'}</Typography>
                <Typography variant="body2" color="text.secondary">{form.description}</Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }} 
                  fullWidth
                  href={form.cta_url || '#'}
                  target="_blank"
                >
                  {form.cta || 'CTA Button'}
                </Button>
              </Box>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddBanner;